import { Badge } from '@gto/ui/badge'
import { Typography } from '@gto/ui/typography'

type StatusVariant =
  | 'LOCKED'
  | 'UNLOCKED'
  | 'PAID'
  | 'REFUNDED'
  | 'REQUESTED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'REVERSED'
  | 'ACTIVE'
  | 'INACTIVE'
  | 'EXPIRED'
  | 'USED_UP'
  // Coupon lifecycle: a redemption is mid-checkout. Deliberately separate from
  // PENDING_APPROVAL, which is a partner-approval state on a different subject.
  | 'PENDING'
  | 'APPROVED'
  | 'PENDING_APPROVAL'
  | 'SUSPENDED'
  | 'DRAFT'
  | 'READY'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'
  | 'PARTIAL'

type BadgeColor = 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info'

const STATUS_COLOR_MAP: Record<StatusVariant, BadgeColor> = {
  LOCKED: 'secondary',
  UNLOCKED: 'info',
  PAID: 'primary',
  REFUNDED: 'error',
  REQUESTED: 'warning',
  REJECTED: 'error',
  CANCELLED: 'secondary',
  REVERSED: 'error',
  ACTIVE: 'primary',
  INACTIVE: 'secondary',
  // The five coupon lifecycle states must be distinguishable at a glance, so
  // EXPIRED does not reuse INACTIVE's grey: both are "not usable", but only one
  // is something an admin did.
  EXPIRED: 'error',
  USED_UP: 'info',
  PENDING: 'warning',
  APPROVED: 'success',
  PENDING_APPROVAL: 'warning',
  SUSPENDED: 'error',
  DRAFT: 'secondary',
  READY: 'info',
  PROCESSING: 'warning',
  COMPLETED: 'success',
  FAILED: 'error',
  PARTIAL: 'warning',
}

const STATUS_LABEL_MAP: Record<StatusVariant, string> = {
  LOCKED: 'Locked',
  UNLOCKED: 'Unlocked',
  PAID: 'Paid',
  REFUNDED: 'Refunded',
  REQUESTED: 'Requested',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
  REVERSED: 'Reversed',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  EXPIRED: 'Expired',
  USED_UP: 'Used up',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  PENDING_APPROVAL: 'Pending',
  SUSPENDED: 'Suspended',
  DRAFT: 'Draft',
  READY: 'Ready',
  PROCESSING: 'Processing',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  PARTIAL: 'Partial',
}

type StatusBadgeProps = {
  status: string
}

export const StatusBadge = ({ status }: StatusBadgeProps) => {
  const color =
    (status in STATUS_COLOR_MAP ? STATUS_COLOR_MAP[status as StatusVariant] : undefined) ??
    'secondary'
  const label =
    (status in STATUS_LABEL_MAP ? STATUS_LABEL_MAP[status as StatusVariant] : undefined) ?? status

  return (
    <span className="inline-flex items-center gap-2">
      <Badge
        color={color}
        variant="dot"
      />
      <Typography
        className="text-[0.75rem] leading-[1]"
        variant="body2"
      >
        {label}
      </Typography>
    </span>
  )
}
